/**
 * Mini Stock Entry Quick Action Component
 * 
 * Single-item Stock Entry creation form for quick actions from Item detail page.
 * 
 * Features:
 * - Stock Entry Type is locked (pre-selected, not editable)
 * - Single item only (itemCode is known from context)
 * - Warehouse selection based on action type (to/from/from_to)
 * - Qty and Basic Rate inputs
 * - Optional toggles (only if fields exist in meta)
 * - Shows only one value (Incoming/Outgoing/Transfer) based on action
 * - Creates Draft Stock Entry and navigates to detail page
 * 
 * IMPORTANT: All fieldnames must be verified in Stock Entry and Stock Entry Detail meta.
 * Do not guess fieldnames - only render fields that exist.
 */

import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Loader2, 
  Warehouse, 
  Package, 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  ArrowDown,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { erp } from "@/lib/erp";
import { routes } from "@/lib/config/routes";
import { toast } from "sonner";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import type { WarehouseMode, ValueDisplayMode } from "./stockActionsConfig";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface MiniStockEntryQuickActionProps {
  itemCode: string;
  warehouseContext: string;
  stockEntryTypeLocked: string;
  warehouseMode: WarehouseMode;
  valueDisplay: ValueDisplayMode;
  onDone: () => void;
  lockWarehouses?: boolean; // If false, allow full warehouse selection (no locked warehouses)
}

/**
 * Get modal tracking key for analytics/state tracking
 */
export function getModalTrackingKey(screen: string, actionId?: string): string {
  if (screen === "menu") return "stock_actions_menu";
  if (screen === "stock_entry_quick_action") return `stock_entry_quick_action_${actionId || "unknown"}`;
  return `stock_actions_${screen}`;
}

export function MiniStockEntryQuickAction({
  itemCode,
  warehouseContext,
  stockEntryTypeLocked,
  warehouseMode,
  valueDisplay,
  onDone,
  lockWarehouses = true, // Default to true for backward compatibility
}: MiniStockEntryQuickActionProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      incomingValue: t("incoming_value", "Incoming Value"),
      outgoingValue: t("outgoing_value", "Outgoing Value"),
      transferValue: t("transfer_value", "Transfer Value"),
      locked: t("locked", "(Locked)"),
      selectLocationEllipsis: t("select_location_ellipsis", "Select location..."),
      selectDestinationEllipsis: t("select_destination_ellipsis", "Select destination..."),
      selectSourceEllipsis: t("select_source_ellipsis", "Select source..."),
      selectLocation: t("select_location", "Select location"),
      selectLocations: t("select_locations", "Select locations"),
      selectedLocation: t("selected_location", "Selected location"),
      selectedLocations: t("selected_locations", "Selected locations"),
      createDraftStockEntry: t("create_draft_stock_entry", "Create Draft Stock Entry"),

      transactionType: t("transaction_type", "Transaction Type"),
      typeLabel: t("type", "Type"),
      itemDetails: t("item_details", "Item Details"),
      quantityAndPrice: t("quantity_and_price", "Quantity & Price"),
      quantity: t("quantity", "Quantity"),
      valuationRate: t("valuation_rate", "Valuation Rate"),
      summary: t("summary", "Summary"),
      currentStock: t("current_stock", "Current Stock"),
      totalAmount: t("total_amount", "Total Amount"),
      value: t("value", "Value"),

      options: t("options", "Options"),
      additionalOptions: t("additional_options", "Additional Options"),
      inspectionRequired: t("inspection_required", "Inspection Required"),
      requireInspectionBeforeReceipt: t("require_inspection_before_receipt", "Require inspection before receipt"),
      applyPutawayRules: t("apply_putaway_rules", "Apply Putaway Rules"),
      applyStorageRulesAutomatically: t("apply_storage_rules_automatically", "Apply storage rules automatically"),
      source: t("source", "Source"),
      destination: t("destination", "Destination"),
      fromLocation: t("from_location", "From Location"),
      toLocation: t("to_location", "To Location"),
      selectSource: t("select_source", "Select source"),
      selectedSource: t("selected_source", "Selected source"),
      selectDestination: t("select_destination", "Select destination"),
      selectedDestination: t("selected_destination", "Selected destination"),
      selectSourceLocationEllipsis: t("select_source_location_ellipsis", "Select source location..."),
      selectDestinationLocationEllipsis: t("select_destination_location_ellipsis", "Select destination location..."),
      unitPrice: t("unit_price", "Unit Price"),
      basicRate: t("basic_rate", "Basic Rate"),
      stockAfterReceipt: t("stock_after_receipt", "Stock After Receipt"),
      stockAfterIssue: t("stock_after_issue", "Stock After Issue"),
      stockAfterTransfer: t("stock_after_transfer", "Stock After Transfer"),
      stockAfterManufacture: t("stock_after_manufacture", "Stock After Manufacture"),
      stockAfterRepack: t("stock_after_repack", "Stock After Repack"),
    };
  }, [language]);

  // Check if this is Material Receipt, Material Issue, Material Transfer, Manufacturing Entry, or Repack Entry for special visual treatment (must be declared early)
  const isMaterialReceipt = stockEntryTypeLocked === "Material Receipt";
  const isMaterialIssue = stockEntryTypeLocked === "Material Issue";
  const isMaterialTransfer = stockEntryTypeLocked === "Material Transfer";
  const isManufacturingEntry = stockEntryTypeLocked === "Manufacture";
  const isRepackEntry = stockEntryTypeLocked === "Repack";
  const hasVisualFlow = isMaterialReceipt || isMaterialIssue || isMaterialTransfer || isManufacturingEntry || isRepackEntry;

  // Form state
  const [qty, setQty] = useState<number>(1);
  const [basicRate, setBasicRate] = useState<number>(0);
  
  // Determine which warehouse should be locked based on action type
  // Material Receipt: lock destination (to_warehouse) - user clicked on destination location
  // Material Issue: lock source (from_warehouse) - user clicked on source location
  // Material Transfer/Manufacturing/Repack: lock source (from_warehouse) - user clicked on source location
  // If lockWarehouses is false, no warehouses are locked (full selection allowed)
  const shouldLockFromWarehouse = lockWarehouses && (isMaterialIssue || isMaterialTransfer || isManufacturingEntry || isRepackEntry);
  const shouldLockToWarehouse = lockWarehouses && isMaterialReceipt;
  
  // Initialize warehouses - locked warehouses are set from context and cannot be changed
  // If lockWarehouses is false, start with empty warehouses for full selection
  const [fromWarehouse, setFromWarehouse] = useState<string>(shouldLockFromWarehouse ? warehouseContext : "");
  const [toWarehouse, setToWarehouse] = useState<string>(shouldLockToWarehouse ? warehouseContext : "");

  // Optional toggles - only show if fields exist in meta
  // IMPORTANT: These will be gated by meta check below
  const [inspectionRequired, setInspectionRequired] = useState(false);
  const [applyPutawayRules, setApplyPutawayRules] = useState(false);
  
  // State for image loading error
  const [imageError, setImageError] = useState(false);
  
  // Fetch Stock Entry meta to verify optional fields exist
  const { data: stockEntryMeta } = useQuery({
    queryKey: ["meta", "Stock Entry"],
    queryFn: () => erp.meta.getDocTypeSchema("Stock Entry"),
  });

  // Fetch Stock Entry Detail meta to verify child table fieldnames
  const { data: stockEntryDetailMeta } = useQuery({
    queryKey: ["meta", "Stock Entry Detail"],
    queryFn: () => erp.meta.getDocTypeSchema("Stock Entry Detail"),
    enabled: !!stockEntryMeta, // Only fetch if parent meta exists
  });

  // Fetch Warehouse meta to build filters matching core Frappe behavior
  const { data: warehouseMeta } = useQuery({
    queryKey: ["meta", "Warehouse"],
    queryFn: () => erp.meta.getDocTypeSchema("Warehouse"),
  });

  // Fetch the context warehouse to get its company (for filtering other warehouses)
  const { data: contextWarehouseDoc } = useQuery({
    queryKey: ["warehouse-detail", warehouseContext],
    queryFn: async () => {
      if (!warehouseContext) return null;
      const client = await import("@/lib/erp/client").then(m => m.getERPClient());
      return client.get<Record<string, any>>({ doctype: "Warehouse", name: warehouseContext });
    },
    enabled: !!warehouseContext,
  });

  // Extract company from context warehouse (if available)
  // NOTE: Must be declared before any hooks that reference it to avoid TDZ errors.
  const contextCompany = contextWarehouseDoc?.company as string | undefined;

  // Fetch item's primary image and name for Material Receipt/Issue visualization
  const { data: itemImage } = useQuery({
    queryKey: ["item-primary-image", itemCode],
    queryFn: async () => {
      if (!itemCode || !hasVisualFlow) return null;
      try {
        const frappe = await import("@/lib/frappe-client").then(m => m.frappe);
        // Fetch item gallery to get primary image
        const galleryData = await frappe.call<{
          item_code: string;
          media: Array<{
            name: string;
            url: string;
            alt: string;
            type: 'image' | 'video';
            isPrimaryImage: boolean;
            isPrimaryVideo: boolean;
            idx: number;
            thumbnailUrl: string | null;
          }>;
          primary_image: string | null;
          primary_video: string | null;
          schema_version: string;
        }>('zaviago_backend.api.item_gallery.get_item_gallery', {
          item_code: itemCode,
          contract: 'v2',
        });
        
        // Use primary_image from response, or find first image from media
        const primaryImageUrl = galleryData?.primary_image || 
          galleryData?.media?.find(item => item.type === 'image')?.url;
        
        if (primaryImageUrl) {
          const { normalizeFileUrl } = await import("@/lib/attachments/utils");
          return normalizeFileUrl(primaryImageUrl);
        }
        return null;
      } catch (error) {
        console.error("[MiniStockEntryQuickAction] Failed to fetch item image:", error);
        return null;
      }
    },
    enabled: !!itemCode && hasVisualFlow,
  });

  // Fetch item document to get standard_rate (basic rate) and item name
  const { data: itemDoc } = useQuery({
    queryKey: ["item-detail", itemCode],
    queryFn: async () => {
      if (!itemCode) return null;
      try {
        return await erp.items.detail(itemCode);
      } catch (error) {
        console.error("[MiniStockEntryQuickAction] Failed to fetch item:", error);
        return null;
      }
    },
    enabled: !!itemCode,
  });

  // Extract item name for Material Receipt visualization
  const itemName = itemDoc?.item_name || itemCode;

  // Track if user manually edited the basic rate (don't overwrite if they did)
  const [userTouchedRate, setUserTouchedRate] = useState(false);

  // Determine which warehouse to use for pricing based on stock entry type
  // Material Receipt uses toWarehouse (t_warehouse), Material Issue uses fromWarehouse (s_warehouse)
  const pricingWarehouse = useMemo(() => {
    if (warehouseMode === "from") {
      return fromWarehouse || "";
    } else if (warehouseMode === "to") {
      return toWarehouse || "";
    } else {
      // from_to: for Material Transfer, prefer source (s_warehouse) for valuation
      return fromWarehouse || toWarehouse || "";
    }
  }, [warehouseMode, fromWarehouse, toWarehouse]);

  // Fetch the selected warehouse's company for pricing (not just context warehouse)
  const { data: pricingWarehouseDoc } = useQuery({
    queryKey: ["warehouse-detail", pricingWarehouse],
    queryFn: async () => {
      if (!pricingWarehouse) return null;
      const client = await import("@/lib/erp/client").then(m => m.getERPClient());
      return client.get<Record<string, any>>({ doctype: "Warehouse", name: pricingWarehouse });
    },
    enabled: !!pricingWarehouse,
  });

  // Company for pricing: use selected warehouse's company, fallback to context company
  const pricingCompany = pricingWarehouseDoc?.company || contextCompany;

  // Reset pricing state when item changes (avoid showing stale price)
  useEffect(() => {
    setBasicRate(0);
    setUserTouchedRate(false);
  }, [itemCode]);

  // Fetch ERPNext-computed basic rate using Stock Entry doc method (same as form)
  const basicRateQuery = useQuery<{ basic_rate: number } | null>({
    queryKey: [
      "stock-entry-basic-rate",
      itemCode,
      stockEntryTypeLocked,
      pricingWarehouse,
      pricingCompany,
      qty,
    ],
    queryFn: async () => {
      if (!itemCode || !stockEntryTypeLocked || !pricingWarehouse || !pricingCompany) {
        return null;
      }
      try {
        const result = await erp.stockEntries.getQuickActionBasicRate({
          item_code: itemCode,
          stock_entry_type: stockEntryTypeLocked,
          warehouse: pricingWarehouse,
          company: pricingCompany,
          qty: qty || 1,
        });
        // Debug logging to see what we're getting from the API
        console.log(`[MiniStockEntryQuickAction] API response for warehouse "${pricingWarehouse}":`, result);
        return result;
      } catch (error) {
        console.error("[MiniStockEntryQuickAction] Failed to fetch basic rate:", error);
        return null;
      }
    },
    enabled:
      !!itemCode &&
      !!stockEntryTypeLocked &&
      !!pricingWarehouse &&
      !!pricingCompany &&
      qty > 0 &&
      !userTouchedRate, // Don't spam if user already overrode
    staleTime: 0, // Always consider data stale to force refetch on warehouse change
    gcTime: 0, // Don't cache - always fetch fresh data (gcTime replaces cacheTime in React Query v5)
  });

  // Prefill unit price from ERPNext only if user hasn't manually edited it
  useEffect(() => {
    if (userTouchedRate) return;
    if (!basicRateQuery.data) return;
    const suggested = basicRateQuery.data.basic_rate;
    if (typeof suggested === "number" && suggested > 0) {
      setBasicRate(suggested);
    }
  }, [basicRateQuery.data, userTouchedRate]);

  // TEMPORARILY DISABLED: Fallback prefill from Item doc
  // This is disabled to see the true backend result without standard_rate override
  // TODO: Re-enable only if we explicitly want standard_rate as fallback when ERP has no valuation
  // useEffect(() => {
  //   if (userTouchedRate) return;

  //   const erpSuggested = basicRateQuery.data?.basic_rate;
  //   // If ERP returned a positive rate, let the other effect handle it
  //   if (typeof erpSuggested === "number" && erpSuggested > 0) return;

  //   if (!itemDoc) return;

  //   // Use standard_rate as fallback (Item doesn't have valuation_rate in the type)
  //   const fallback =
  //     typeof itemDoc.standard_rate === "number" && itemDoc.standard_rate > 0
  //       ? itemDoc.standard_rate
  //       : 0;

  //   if (fallback > 0) {
  //     setBasicRate(fallback);
  //   }
  // }, [basicRateQuery.data, itemDoc, userTouchedRate]);

  // Reset image error when image URL changes
  useEffect(() => {
    setImageError(false);
  }, [itemImage]);

  // Fetch current stock in warehouse for Material Receipt/Issue/Transfer/Manufacturing/Repack summary
  const { data: currentStock } = useQuery({
    queryKey: ["item-stock-warehouse", itemCode, toWarehouse, fromWarehouse, isMaterialReceipt, isMaterialIssue, isMaterialTransfer, isManufacturingEntry, isRepackEntry],
    queryFn: async () => {
      if (!itemCode || !hasVisualFlow) return null;
      const warehouse = isMaterialReceipt ? toWarehouse : (isMaterialIssue || isMaterialTransfer || isManufacturingEntry || isRepackEntry) ? fromWarehouse : null;
      if (!warehouse) return null;
      try {
        // Use ERP API to get stock data
        const stockData = await erp.items.stock(itemCode);
        const warehouseStock = stockData?.find(bin => bin.warehouse === warehouse);
        return warehouseStock?.actual_qty ?? 0;
      } catch (error) {
        console.error("[MiniStockEntryQuickAction] Failed to fetch stock:", error);
        return null;
      }
    },
    enabled: !!itemCode && hasVisualFlow && ((isMaterialReceipt && !!toWarehouse) || ((isMaterialIssue || isMaterialTransfer || isManufacturingEntry || isRepackEntry) && !!fromWarehouse)),
  });

  // Calculate stock after transaction
  const stockAfterTransaction = useMemo(() => {
    if (currentStock === null || currentStock === undefined) return null;
    if (isMaterialReceipt) {
      return (currentStock ?? 0) + (qty || 0);
    } else if (isMaterialIssue || isMaterialTransfer || isManufacturingEntry || isRepackEntry) {
      return (currentStock ?? 0) - (qty || 0);
    }
    return null;
  }, [currentStock, qty, isMaterialReceipt, isMaterialIssue, isMaterialTransfer, isManufacturingEntry, isRepackEntry]);

  // Build Warehouse filters matching core Frappe behavior (meta-gated)
  const warehouseDefaultFilters = useMemo(() => {
    const fields = warehouseMeta?.fields ?? [];
    const hasDisabled = fields.some((f) => f.fieldname === "disabled");
    const hasIsGroup = fields.some((f) => f.fieldname === "is_group");
    const hasCompany = fields.some((f) => f.fieldname === "company");

    const filters: Record<string, any> = {};
    if (hasDisabled) filters.disabled = 0;
    if (hasIsGroup) filters.is_group = 0;
    // Add company filter if field exists and we have a company from context
    if (hasCompany && contextCompany) filters.company = contextCompany;

    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [warehouseMeta, contextCompany]);

  // Check if optional fields exist in meta (gate rendering)
  const hasInspectionRequired = useMemo(() => {
    if (!stockEntryMeta) return false;
    return stockEntryMeta.fields.some(f => f.fieldname === "inspection_required");
  }, [stockEntryMeta]);

  const hasApplyPutawayRules = useMemo(() => {
    if (!stockEntryMeta) return false;
    return stockEntryMeta.fields.some(f => f.fieldname === "apply_putaway_rules");
  }, [stockEntryMeta]);

  // Check if header warehouse fields exist in meta (gate payload)
  const hasToWarehouse = useMemo(() => {
    if (!stockEntryMeta) return false;
    return stockEntryMeta.fields.some(f => f.fieldname === "to_warehouse");
  }, [stockEntryMeta]);

  const hasFromWarehouse = useMemo(() => {
    if (!stockEntryMeta) return false;
    return stockEntryMeta.fields.some(f => f.fieldname === "from_warehouse");
  }, [stockEntryMeta]);

  // Verify child table fieldnames exist
  const childTableFieldnames = useMemo(() => {
    if (!stockEntryDetailMeta) return { item_code: false, qty: false, basic_rate: false, s_warehouse: false, t_warehouse: false };
    
    const fieldnames = new Set(stockEntryDetailMeta.fields.map(f => f.fieldname));
    return {
      item_code: fieldnames.has("item_code"),
      qty: fieldnames.has("qty"),
      basic_rate: fieldnames.has("basic_rate"),
      s_warehouse: fieldnames.has("s_warehouse"),
      t_warehouse: fieldnames.has("t_warehouse"),
    };
  }, [stockEntryDetailMeta]);

  // Computed value (only show one based on valueDisplay)
  const computedValue = useMemo(() => {
    const v = (Number(qty) || 0) * (Number(basicRate) || 0);
    return Number.isFinite(v) ? v : 0;
  }, [qty, basicRate]);

  const valueLabel =
    valueDisplay === "incoming"
      ? ui.incomingValue
      : valueDisplay === "outgoing"
      ? ui.outgoingValue
      : ui.transferValue;

  // Validation
  const canSubmit = useMemo(() => {
    if (qty <= 0) return false;
    if (warehouseMode === "from" && !fromWarehouse) return false;
    if (warehouseMode === "to" && !toWarehouse) return false;
    if (warehouseMode === "from_to" && (!fromWarehouse || !toWarehouse)) return false;
    return true;
  }, [qty, warehouseMode, fromWarehouse, toWarehouse]);

  // Create Draft Stock Entry mutation
  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      return erp.stockEntries.create(payload);
    },
    onSuccess: (created) => {
      if (created.name) {
        const detailRoute = routes.stockEntry.detail(created.name);
        // Toast before navigate — component may unmount on route change
        toast.success("Draft created", {
          description: "Review and submit when ready",
          action: {
            label: "View & Submit",
            onClick: () => navigate(detailRoute),
          },
        });
        navigate(detailRoute);
      }
      onDone();
    },
    onError: (error) => {
      console.error("[MiniStockEntryQuickAction] Failed to create Stock Entry:", error);
      const message = error instanceof Error ? error.message : String(error);
      toast.error(message || "Failed to create Stock Entry");
    },
  });

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit || !stockEntryDetailMeta) return;

    // Build minimal payload - backend validates everything
    // Only use fieldnames that we verified exist in meta
    const itemsRow: Record<string, any> = {};

    // Add verified child table fields
    if (childTableFieldnames.item_code) {
      itemsRow.item_code = itemCode;
    }
    if (childTableFieldnames.qty) {
      itemsRow.qty = qty;
    }
    if (childTableFieldnames.basic_rate) {
      itemsRow.basic_rate = basicRate;
    }

    // Add warehouse fields based on mode
    if (warehouseMode === "from" || warehouseMode === "from_to") {
      if (childTableFieldnames.s_warehouse) {
        itemsRow.s_warehouse = fromWarehouse;
      }
    }
    if (warehouseMode === "to" || warehouseMode === "from_to") {
      if (childTableFieldnames.t_warehouse) {
        itemsRow.t_warehouse = toWarehouse;
      }
    }

    const payload: Record<string, any> = {
      stock_entry_type: stockEntryTypeLocked,
      docstatus: 0, // Draft
      items: [itemsRow],
    };

    // Add header warehouse fields (meta-gated)
    if (warehouseMode === "to" || warehouseMode === "from_to") {
      if (hasToWarehouse && toWarehouse) {
        payload.to_warehouse = toWarehouse;
      }
    }
    if (warehouseMode === "from" || warehouseMode === "from_to") {
      if (hasFromWarehouse && fromWarehouse) {
        payload.from_warehouse = fromWarehouse;
      }
    }

    // Add optional flags only if fields exist in meta
    if (hasInspectionRequired) {
      payload.inspection_required = inspectionRequired ? 1 : 0;
    }
    if (hasApplyPutawayRules) {
      payload.apply_putaway_rules = applyPutawayRules ? 1 : 0;
    }

    // Log payload before mutation for debugging
    console.log("[MiniStockEntryQuickAction] Submitting payload:", JSON.stringify(payload, null, 2));

    createMutation.mutate(payload);
  };

  // Build field groups for rendering
  const fieldGroups = useMemo(() => {
    const groups: Array<{ 
      label: string; 
      icon?: React.ReactNode;
      fields: Array<{ key: string; label: string; element: React.ReactNode }> 
    }> = [];

    // Group 1: Stock Entry Type (read-only, locked) - only show if not Material Receipt/Issue (we'll show it in header)
    if (!hasVisualFlow) {
      groups.push({
        label: ui.transactionType,
        fields: [
          {
            key: "stock_entry_type",
            label: ui.typeLabel,
            element: (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-sm font-medium px-3 py-1">
                  {stockEntryTypeLocked}
                </Badge>
                <span className="text-xs text-muted-foreground">{ui.locked}</span>
              </div>
            ),
          },
        ],
      });
    }

    // Group 2: Options (toggles) - only if fields exist
    if (hasInspectionRequired || hasApplyPutawayRules) {
      const optionFields: Array<{ key: string; label: string; element: React.ReactNode }> = [];
      
      if (hasInspectionRequired) {
        optionFields.push({
          key: "inspection_required",
          label: isMaterialReceipt ? ui.requireInspectionBeforeReceipt : ui.inspectionRequired,
          element: (
            <Switch
              checked={inspectionRequired}
              onCheckedChange={setInspectionRequired}
            />
          ),
        });
      }
      
      if (hasApplyPutawayRules) {
        optionFields.push({
          key: "apply_putaway_rules",
          label: isMaterialReceipt ? ui.applyStorageRulesAutomatically : ui.applyPutawayRules,
          element: (
            <Switch
              checked={applyPutawayRules}
              onCheckedChange={setApplyPutawayRules}
            />
          ),
        });
      }

      if (optionFields.length > 0) {
        groups.push({
          label: isMaterialReceipt ? ui.additionalOptions : ui.options,
          fields: optionFields,
        });
      }
    }

    // Group 3: Location/Destination
    // For location cards (lockWarehouses=true): Hide locked warehouses to reduce confusion
    // - Material Receipt: Hide destination (locked)
    // - Material Issue: Hide source (locked)
    // - Material Transfer/Manufacturing/Repack: Hide source (locked)
    const warehouseFields: Array<{ key: string; label: string; element: React.ReactNode }> = [];
    
    // Only show from_warehouse if it's not locked (or if lockWarehouses is false, show it)
    if ((warehouseMode === "from" || warehouseMode === "from_to") && !shouldLockFromWarehouse) {
      warehouseFields.push({
        key: "from_warehouse",
        label: (isMaterialTransfer || isManufacturingEntry || isRepackEntry) ? ui.source : ui.fromLocation,
        element: (
          <div className="flex items-center gap-2 flex-1 max-w-[60%] justify-end">
            <LinkFieldSearch
              doctype="Warehouse"
              referenceDoctype="Stock Entry"
              value={fromWarehouse || null}
              onChange={(value) => setFromWarehouse(value || "")}
              placeholder={(isMaterialTransfer || isManufacturingEntry || isRepackEntry) ? ui.selectSourceEllipsis : ui.selectSourceLocationEllipsis}
              className="flex-1 !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
              data-field="from_warehouse"
              defaultFilters={warehouseDefaultFilters}
            />
          </div>
        ),
      });
    }
    
    // Only show to_warehouse if it's not locked (or if lockWarehouses is false, show it)
    if ((warehouseMode === "to" || warehouseMode === "from_to") && !shouldLockToWarehouse) {
      warehouseFields.push({
        key: "to_warehouse",
        label: isMaterialReceipt
          ? ui.destination
          : isMaterialIssue
            ? ui.source
            : isMaterialTransfer
              ? ui.destination
              : isManufacturingEntry
                ? ui.destination
                : isRepackEntry
                  ? ui.destination
                  : ui.toLocation,
        element: (
          <div className="flex items-center gap-2 flex-1 max-w-[60%] justify-end">
            <LinkFieldSearch
              doctype="Warehouse"
              referenceDoctype="Stock Entry"
              value={toWarehouse || null}
              onChange={(value) => setToWarehouse(value || "")}
              placeholder={
                isMaterialReceipt
                  ? ui.selectDestinationEllipsis
                  : isMaterialIssue
                    ? ui.selectSourceEllipsis
                    : isMaterialTransfer
                      ? ui.selectDestinationEllipsis
                      : isManufacturingEntry
                        ? ui.selectDestinationEllipsis
                        : isRepackEntry
                          ? ui.selectDestinationEllipsis
                          : ui.selectLocationEllipsis
              }
              className="flex-1 !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
              data-field="to_warehouse"
              defaultFilters={warehouseDefaultFilters}
            />
          </div>
        ),
      });
    }

    if (warehouseFields.length > 0) {
      // Determine guided label based on whether destination is selected
      const hasDestination = warehouseMode === "to" ? !!toWarehouse : (warehouseMode === "from_to" ? (!!fromWarehouse && !!toWarehouse) : !!fromWarehouse);
      const guidedLabel = isMaterialReceipt 
        ? (hasDestination ? ui.selectedDestination : ui.selectDestination)
        : isMaterialIssue
        ? (hasDestination ? ui.selectedSource : ui.selectSource)
        : (isMaterialTransfer || isManufacturingEntry || isRepackEntry)
        ? (hasDestination ? ui.selectedLocations : ui.selectLocations)
        : (hasDestination ? ui.selectedLocation : ui.selectLocation);
      
      groups.push({
        label: guidedLabel,
        icon: undefined,
        fields: warehouseFields,
      });
    }

    // Group 4: Quantity and Rate
      groups.push({
        label: hasVisualFlow ? ui.quantityAndPrice : ui.itemDetails,
        icon: undefined,
      fields: [
        {
          key: "qty",
          label: ui.quantity,
          element: (
            <Input
              type="number"
              min={0}
              step="0.01"
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
            />
          ),
        },
        {
          key: "basic_rate",
          label: hasVisualFlow ? ui.unitPrice : ui.basicRate,
          element: (
            <Input
              type="number"
              min={0}
              step="0.01"
              value={basicRate}
              onChange={(e) => {
                setUserTouchedRate(true);
                setBasicRate(Number(e.target.value));
              }}
              className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
            />
          ),
        },
      ],
    });

    // Group 5: Summary (read-only display) - Material Receipt/Issue shows stock counts + value
    if (hasVisualFlow) {
      groups.push({
        label: ui.summary,
        icon: undefined,
        fields: [
          // Stock Count - Before
          {
            key: "stock_before",
            label: ui.currentStock,
            element: (
              <div className="text-[17px] text-gray-900 dark:text-gray-100 font-normal text-right">
                {currentStock !== null && currentStock !== undefined 
                  ? currentStock.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                  : "—"}
              </div>
            ),
          },
          // Stock Count - After
          {
            key: "stock_after",
            label: isMaterialReceipt
              ? ui.stockAfterReceipt
              : isMaterialIssue
                ? ui.stockAfterIssue
                : isMaterialTransfer
                  ? ui.stockAfterTransfer
                  : isManufacturingEntry
                    ? ui.stockAfterManufacture
                    : ui.stockAfterRepack,
            element: (
              <div className="flex items-center gap-2 justify-end">
                {isMaterialReceipt ? (
                  <TrendingUp className="w-4 h-4 text-green-500 dark:text-green-400" />
                ) : isMaterialIssue ? (
                  <TrendingDown className="w-4 h-4 text-red-500 dark:text-red-400" />
                ) : isMaterialTransfer ? (
                  <TrendingDown className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                ) : isManufacturingEntry ? (
                  <TrendingDown className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                )}
                <div className="text-[17px] text-gray-900 dark:text-gray-100 font-semibold text-right">
                  {stockAfterTransaction !== null 
                    ? stockAfterTransaction.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                    : "—"}
                </div>
              </div>
            ),
          },
          // Total Value
          {
            key: "value",
            label: ui.totalAmount,
            element: (
              <div className="flex items-center gap-2 justify-end">
                <DollarSign className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div className="text-[17px] text-gray-900 dark:text-gray-100 font-semibold text-right">
                  {computedValue.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            ),
          },
        ],
      });
    } else {
      // For non-Material Receipt, keep original value display
      groups.push({
        label: ui.value,
        icon: undefined,
        fields: [
          {
            key: "value",
            label: valueLabel,
            element: (
              <div className="flex items-center gap-2 justify-end">
                <div className="text-[17px] text-gray-900 dark:text-gray-100 font-semibold text-right">
                  {computedValue.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            ),
          },
        ],
      });
    }

    return groups;
  }, [
    isMaterialReceipt,
    isMaterialIssue,
    isMaterialTransfer,
    isManufacturingEntry,
    isRepackEntry,
    hasVisualFlow,
    stockEntryTypeLocked,
    hasInspectionRequired,
    hasApplyPutawayRules,
    inspectionRequired,
    applyPutawayRules,
    warehouseMode,
    fromWarehouse,
    toWarehouse,
    warehouseDefaultFilters,
    qty,
    basicRate,
    valueLabel,
    computedValue,
    currentStock,
    stockAfterTransaction,
    shouldLockFromWarehouse,
    shouldLockToWarehouse,
  ]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
        <div className="pb-8 px-10 pt-2">
          {/* Material Receipt Visual Flow - Apple Style */}
          {isMaterialReceipt && (
            <div className="mb-6">
              <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200/30 dark:border-gray-800/30">
                {/* Product Image → Warehouse Flow */}
                <div className="flex flex-col gap-3">
                  {/* Main Flow Row - Relative for absolute centering */}
                  <div className="relative flex items-center justify-between min-h-[80px]">
                    {/* Product Image with Quantity Badge and Name */}
                    <div className="flex-shrink-0 flex items-center gap-3">
                      <div className="relative flex items-center justify-center">
                      {itemImage && !imageError ? (
                        <div className="w-20 h-20 rounded-md bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                          <img
                            src={itemImage}
                            alt="Product"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1.5 -right-1.5 min-w-[28px] h-7 px-1.5 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[13px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-md bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 flex items-center justify-center shadow-sm relative">
                          <Package className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1.5 -right-1.5 min-w-[28px] h-7 px-1.5 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[13px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      )}
                      </div>
                      {/* Product Name */}
                      {itemName && (
                        <div className="text-left">
                          <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-tight max-w-[140px] truncate">
                            {itemName}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Arrow pointing right with inspection indicator - Always centered */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-2 z-10">
                      <ArrowDown className="w-7 h-7 text-gray-400 dark:text-gray-500 rotate-[-90deg]" />
                      {inspectionRequired && (
                        <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                      )}
                    </div>
                    
                    {/* Destination Location */}
                    <div className="flex-shrink-0 flex items-center gap-3">
                      <div className="w-12 h-12 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-6 h-6 text-[#007AFF]" />
                      </div>
                      <div className="text-left">
                        <div className="text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Destination Location
                        </div>
                        <div className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">
                          {toWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Inspection Required Text Row - Centered Below */}
                  {inspectionRequired && (
                    <div className="flex items-center justify-center pt-2">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400 leading-tight">
                          Inspection required before receipt
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Material Issue Visual Flow - Apple Style */}
          {isMaterialIssue && (
            <div className="mb-6">
              <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200/30 dark:border-gray-800/30">
                {/* Source Location → Product Image Flow */}
                <div className="flex flex-col gap-3">
                  {/* Main Flow Row - Relative for absolute centering */}
                  <div className="relative flex items-center justify-between min-h-[80px]">
                    {/* Source Location */}
                    <div className="flex-shrink-0 flex items-center gap-3">
                      <div className="w-12 h-12 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-6 h-6 text-[#FF3B30]" />
                      </div>
                      <div className="text-left">
                        <div className="text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Source Location
                        </div>
                        <div className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">
                          {fromWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Arrow pointing right with inspection indicator - Always centered */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-2 z-10">
                      <ArrowRight className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                      {inspectionRequired && (
                        <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                      )}
                    </div>
                    
                    {/* Product Image with Quantity Badge and Name */}
                    <div className="flex-shrink-0 flex items-center gap-3">
                      <div className="relative flex items-center justify-center">
                      {itemImage && !imageError ? (
                        <div className="w-20 h-20 rounded-md bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                          <img
                            src={itemImage}
                            alt="Product"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1.5 -right-1.5 min-w-[28px] h-7 px-1.5 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[13px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-md bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 flex items-center justify-center shadow-sm relative">
                          <Package className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1.5 -right-1.5 min-w-[28px] h-7 px-1.5 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[13px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      )}
                      </div>
                      {/* Product Name */}
                      {itemName && (
                        <div className="text-left">
                          <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-tight max-w-[140px] truncate">
                            {itemName}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Inspection Required Text Row - Centered Below */}
                  {inspectionRequired && (
                    <div className="flex items-center justify-center pt-2">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400 leading-tight">
                          Inspection required before issue
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Material Transfer Visual Flow - Apple Style */}
          {isMaterialTransfer && (
            <div className="mb-6">
              <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200/30 dark:border-gray-800/30">
                {/* Source Location → Product Image → Destination Location Flow */}
                <div className="flex flex-col gap-2">
                  {/* Main Flow Row - Flex layout with three sections */}
                  <div className="flex items-center justify-between min-h-[60px] gap-2">
                    {/* Source Location */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-5 h-5 text-[#FF3B30]" />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                          Source Location
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {fromWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Arrow pointing right */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      {inspectionRequired && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 ml-1.5" />
                      )}
                    </div>
                    
                    {/* Product Image with Quantity Badge (no name) */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <div className="relative flex items-center justify-center">
                      {itemImage && !imageError ? (
                        <div className="w-16 h-16 rounded bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                          <img
                            src={itemImage}
                            alt="Product"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[11px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 flex items-center justify-center shadow-sm relative">
                          <Package className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[11px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                    
                    {/* Arrow pointing right to destination */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                    </div>
                    
                    {/* Destination Location */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                          Destination Location
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {toWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Inspection Required Text Row - Centered Below */}
                  {inspectionRequired && (
                    <div className="flex items-center justify-center pt-2">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400 leading-tight">
                          Inspection required before transfer
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Manufacturing Entry Visual Flow - Apple Style */}
          {isManufacturingEntry && (
            <div className="mb-6">
              <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200/30 dark:border-gray-800/30">
                {/* Source Location → Product Image → Destination Location Flow */}
                <div className="flex flex-col gap-2">
                  {/* Main Flow Row - Flex layout with three sections */}
                  <div className="flex items-center justify-between min-h-[60px] gap-2">
                    {/* Source Location */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-5 h-5 text-[#FF3B30]" />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                          Source Location
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {fromWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Arrow pointing right */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      {inspectionRequired && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 ml-1.5" />
                      )}
                    </div>
                    
                    {/* Product Image with Quantity Badge (no name) */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <div className="relative flex items-center justify-center">
                      {itemImage && !imageError ? (
                        <div className="w-16 h-16 rounded bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                          <img
                            src={itemImage}
                            alt="Product"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[11px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 flex items-center justify-center shadow-sm relative">
                          <Package className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[11px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                    
                    {/* Arrow pointing right to destination */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                    </div>
                    
                    {/* Destination Location */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                          Destination Location
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {toWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Inspection Required Text Row - Centered Below */}
                  {inspectionRequired && (
                    <div className="flex items-center justify-center pt-2">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400 leading-tight">
                          Inspection required before manufacture
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Repack Entry Visual Flow - Apple Style */}
          {isRepackEntry && (
            <div className="mb-6">
              <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200/30 dark:border-gray-800/30">
                {/* Source Location → Product Image → Destination Location Flow */}
                <div className="flex flex-col gap-2">
                  {/* Main Flow Row - Flex layout with three sections */}
                  <div className="flex items-center justify-between min-h-[60px] gap-2">
                    {/* Source Location */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-5 h-5 text-[#FF3B30]" />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                          Source Location
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {fromWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Arrow pointing right */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      {inspectionRequired && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 ml-1.5" />
                      )}
                    </div>
                    
                    {/* Product Image with Quantity Badge (no name) */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <div className="relative flex items-center justify-center">
                      {itemImage && !imageError ? (
                        <div className="w-16 h-16 rounded bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                          <img
                            src={itemImage}
                            alt="Product"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[11px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 flex items-center justify-center shadow-sm relative">
                          <Package className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                          {/* Quantity Badge - Apple Style Yellow */}
                          <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                            <span className="text-[11px] font-semibold text-gray-900 leading-none tracking-tight">
                              {qty > 99 ? '99+' : qty}
                            </span>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                    
                    {/* Arrow pointing right to destination */}
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                    </div>
                    
                    {/* Destination Location */}
                    <div className="flex-shrink-0 flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                        <Warehouse className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                          Destination Location
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {toWarehouse || (
                            <span className="text-gray-400 dark:text-gray-500 italic">{ui.selectLocationEllipsis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Inspection Required Text Row - Centered Below */}
                  {inspectionRequired && (
                    <div className="flex items-center justify-center pt-2">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400 leading-tight">
                          Inspection required before repack
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="py-2 space-y-5">
            {fieldGroups.map((group) => (
              <div
                key={group.label}
                className={cn(
                  "bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40",
                  hasVisualFlow && group.icon && "shadow-sm"
                )}
              >
                {/* Group Header (Material Receipt/Issue only) - Subtle Apple Style */}
                {hasVisualFlow && (
                  <div className="px-5 py-2.5 border-b border-gray-200/30 dark:border-gray-700/30">
                    <div className="flex items-center gap-2">
                      {group.icon && group.icon}
                      <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400 tracking-[-0.1px]">
                        {group.label}
                      </span>
                    </div>
                  </div>
                )}
                
                {group.fields.map((field, fieldIndex) => {
                  const isLastInGroup = fieldIndex === group.fields.length - 1;
                  return (
                    <div
                      key={field.key}
                      className={cn(
                        "flex items-center justify-between min-h-[52px] px-5",
                        !isLastInGroup && "border-b border-gray-200/40 dark:border-gray-700/40"
                      )}
                    >
                      <label className={cn(
                        "text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0",
                        isMaterialReceipt && group.icon && "text-[15px]"
                      )}>
                        {!isMaterialReceipt || !group.icon ? field.label : (
                          <span className="text-gray-600 dark:text-gray-400">{field.label}</span>
                        )}
                        {(field.key === "from_warehouse" || field.key === "to_warehouse" || field.key === "qty") && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </label>
                      <div className="flex-1 max-w-[60%] flex justify-end">
                        {field.element}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom fade-out gradient */}
        <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
      </div>

      {/* Sticky Footer Button */}
      <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
        <Button
          type="submit"
          disabled={!canSubmit || createMutation.isPending}
          className={cn(
            "w-full text-[17px] font-semibold text-white px-7 py-3.5 h-auto rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all",
            isMaterialReceipt 
              ? "bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm hover:shadow-md"
              : "bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm"
          )}
        >
          {createMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating...
            </>
          ) : (
            <div className="flex items-center justify-center gap-2">
              {isMaterialReceipt && (
                <CheckCircle2 className="w-5 h-5" />
              )}
              <span>{ui.createDraftStockEntry}</span>
            </div>
          )}
        </Button>
      </div>
    </form>
  );
}

